import { useState, useEffect } from "react";
import MindrailLogo from "./MindrailLogo";
import { externalLinkProps, installCta } from "../../lib/funnelLinks";

export default function Nav() {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleNavClick = (event, href) => {
    if (href !== "#pricing") return;

    const pricingSection = document.querySelector("[data-testid='pro-pricing-section']");
    const nav = document.querySelector("[data-testid='site-nav']");
    if (!pricingSection) return;

    event.preventDefault();

    const pricingShell = pricingSection.querySelector("[data-testid='pro-pricing-shell']");
    const target = pricingShell || pricingSection;
    const targetRect = target.getBoundingClientRect();
    const navHeight = nav?.getBoundingClientRect().height ?? 96;
    const availableHeight = window.innerHeight - navHeight;
    const desiredTop = navHeight + Math.max((availableHeight - targetRect.height) / 2, 28);
    const targetTop = window.scrollY + targetRect.top - desiredTop;
    const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    window.history.pushState(null, "", href);
    window.scrollTo({
      top: Math.max(0, targetTop),
      behavior: reduceMotion ? "auto" : "smooth",
    });
  };

  return (
    <header
      data-testid="site-nav"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-[#F8FAFC]/85 backdrop-blur-md border-b border-[#E2E8F0]"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 h-24 flex items-center justify-between">
        <a href="#top" data-testid="nav-logo" className="group">
          <MindrailLogo size={84} />
        </a>

        <nav className="hidden md:flex items-center gap-1.5 bg-white/70 border border-[#E2E8F0] rounded-full px-2 py-1.5 backdrop-blur-sm">
          {[
            { href: "#features", label: "Features", id: "nav-features" },
            { href: "#how", label: "How it works", id: "nav-how" },
            { href: "#pricing", label: "Pricing", id: "nav-pricing" },
            { href: "#privacy", label: "Privacy", id: "nav-privacy" },
            { href: "#walkthrough", label: "About", id: "nav-about" },
          ].map((l) => (
            <a
              key={l.id}
              href={l.href}
              data-testid={l.id}
              onClick={(event) => handleNavClick(event, l.href)}
              className="px-3.5 py-1.5 text-sm text-[#475569] hover:text-[#0F172A] hover:bg-white rounded-full transition-colors"
            >
              {l.label}
            </a>
          ))}
        </nav>

        <a
          href={installCta.href}
          data-testid="nav-cta"
          aria-label={installCta.label}
          className="inline-flex items-center gap-2 rounded-full bg-[#2563EB] text-white px-4 py-2 text-sm font-medium hover:bg-[#1d4ed8] transition-colors shadow-[0_4px_12px_rgba(37,99,235,0.25)]"
          {...externalLinkProps(installCta.external)}
        >
          <span className="hidden sm:inline">{installCta.label}</span>
          <span className="sm:hidden">{installCta.shortLabel}</span>
        </a>
      </div>
    </header>
  );
}
